import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, 
  Plus, 
  Edit2, 
  Trash2, 
  Calendar,
  Save,
  X,
  TrendingDown,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';
import costService from '../services/costService';
import eventService from '../services/eventService';
import authService from '../services/authService';
import bookingService from '../services/bookingService';

const styles = `
.costs-management-page {
  min-height: 100vh;
  background: linear-gradient(to bottom right, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%);
  padding: 2rem 0;
}

.costs-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.costs-header {
  background: linear-gradient(135deg, #2c5f6f 0%, #1a3d47 100%);
  color: white;
  padding: 2.5rem;
  border-radius: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateX(-5px);
}

.costs-title {
  font-size: 2.5rem;
  margin: 0;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.costs-subtitle {
  font-size: 1.1rem;
  margin: 0.5rem 0 0 0;
  opacity: 0.95;
}

.costs-sections {
  display: grid;
  gap: 2rem;
}

.cost-section {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.section-header {
  background: linear-gradient(to right, #f8fafc 0%, #f1f5f9 100%);
  padding: 1.5rem 2rem;
  border-bottom: 2px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
}

.add-cost-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #2c5f6f 0%, #1a3d47 100%);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-cost-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(44, 95, 111, 0.3);
}

.section-content {
  padding: 2rem;
}

.cost-form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 0.75rem;
  border: 2px dashed #cbd5e1;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #475569;
}

.form-input,
.form-select {
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #2c5f6f;
  box-shadow: 0 0 0 3px rgba(44, 95, 111, 0.1);
}

.form-actions {
  display: flex;
  gap: 1rem;
  grid-column: 1 / -1;
}

.btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-secondary {
  background: #e2e8f0;
  color: #475569;
}

.btn-secondary:hover {
  background: #cbd5e1;
}

.costs-list {
  display: grid;
  gap: 1rem;
}

.cost-item {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr auto;
  gap: 1rem;
  align-items: center;
  padding: 1.5rem;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 0.75rem;
  transition: all 0.3s ease;
}

.cost-item:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.cost-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.cost-name {
  font-weight: 700;
  color: #0f172a;
  font-size: 1.1rem;
}

.cost-desc {
  font-size: 0.9rem;
  color: #64748b;
}

.cost-value {
  font-size: 1.5rem;
  font-weight: 800;
  color: #ef4444;
}

.cost-date {
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cost-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn.edit {
  background: #dbeafe;
  color: #1e40af;
}

.action-btn.edit:hover {
  background: #3b82f6;
  color: white;
}

.action-btn.delete {
  background: #fee2e2;
  color: #991b1b;
}

.action-btn.delete:hover {
  background: #ef4444;
  color: white;
}

.no-costs {
  text-align: center;
  padding: 3rem;
  color: #94a3b8;
}

.no-costs svg {
  margin-bottom: 1rem;
  opacity: 0.5;
}

.alert {
  display: flex;
  align-items: start;
  gap: 1rem;
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
}

.alert-info {
  background: #dbeafe;
  color: #1e40af;
  border: 1px solid #93c5fd;
}

.loading {
  text-align: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: #64748b;
}

.date-options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.date-option {
  padding: 0.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 0.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
}

.date-option:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.date-option.selected {
  background: #2c5f6f;
  color: white;
  border-color: #2c5f6f;
}

.multi-day-info {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.multi-day-info h4 {
  margin: 0 0 0.5rem 0;
  color: #0369a1;
  font-size: 0.9rem;
}

.multi-day-info p {
  margin: 0;
  font-size: 0.8rem;
  color: #64748b;
}

.booking-info {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.booking-info h4 {
  margin: 0 0 0.5rem 0;
  color: #0369a1;
  font-size: 0.9rem;
}

.booking-info p {
  margin: 0;
  font-size: 0.8rem;
  color: #64748b;
}

.multi-day-costs-list {
  margin: 1rem 0;
  border: 2px dashed #cbd5e1;
  border-radius: 0.5rem;
  padding: 1rem;
  background: #f8fafc;
}

.multi-day-cost-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
}

.multi-day-cost-info {
  flex: 1;
}

.multi-day-cost-date {
  font-weight: 600;
  color: #2c5f6f;
}

.multi-day-cost-value {
  font-weight: 700;
  color: #dc2626;
  margin: 0 1rem;
}

.multi-day-actions {
  display: flex;
  gap: 0.5rem;
}

.save-all-button {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.save-all-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
}

.cost-group {
  margin-bottom: 2rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  overflow: hidden;
}

.cost-group-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.cost-group-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cost-group-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0.25rem 0 0 0;
}

.continue-button {
  background: #f1f5f9;
  color: #475569;
  border: 2px solid #e2e8f0;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  margin-left: 1rem;
}

.continue-button:hover {
  background: #e2e8f0;
}

.description-suggestions {
  position: relative;
  width: 100%;
}

.suggestions-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 0.25rem;
}

.suggestion-item {
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.suggestion-item:hover {
  background: #f8fafc;
  color: #2c5f6f;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestions-header {
  padding: 0.5rem 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
}

.description-input-container {
  position: relative;
}

@media (max-width: 768px) {
  .cost-item {
    grid-template-columns: 1fr;
  }
  
  .cost-actions {
    justify-self: end;
  }

  .date-options {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }

  .multi-day-cost-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .multi-day-cost-value {
    margin: 0;
  }

  .form-actions {
    flex-direction: column;
  }

  .continue-button {
    margin-left: 0;
    margin-top: 0.5rem;
  }
}
`;

const DescriptionSuggestions = ({ suggestions, onSelect, visible, formType }) => {
  if (!visible || suggestions.length === 0) return null;

  return (
    <div className="suggestions-list">
      <div className="suggestions-header">
        Descrições anteriores ({suggestions.length})
      </div>
      {suggestions.map((suggestion, index) => (
        <div
          key={index}
          className="suggestion-item"
          onClick={() => onSelect(suggestion, formType)}
        >
          {suggestion}
        </div>
      ))}
    </div>
  );
};

export default function CostsManagementPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [costs, setCosts] = useState({ eventCosts: [], serviceCosts: [], bookingCosts: [] });
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  
  const [showEventForm, setShowEventForm] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  
  const [newEventCost, setNewEventCost] = useState({ 
    eventId: '', 
    cost: '', 
    description: '',
    specificDate: ''
  });
  const [newBookingCost, setNewBookingCost] = useState({ 
    cost: '', 
    description: '' 
  });
  
  const [selectedEventDates, setSelectedEventDates] = useState([]);
  const [multiDayCosts, setMultiDayCosts] = useState([]);
  
  const [descriptionSuggestions, setDescriptionSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentFormType, setCurrentFormType] = useState('');

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.description-input-container')) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const checkAuthAndLoadData = async () => {
    try {
      const user = await authService.getUser();
      if (!user || !user.isAdmin) {
        navigate('/');
        return;
      }
      await loadData();
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      navigate('/');
    }
  };

  const generateEventDates = (startDate, endDate) => {
    const dates = [];
    const currentDate = new Date(startDate);
    const finalDate = new Date(endDate);
    
    while (currentDate <= finalDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  const groupCostsByEvent = (costs) => {
    const grouped = {};
    
    costs.forEach(cost => {
      if (!grouped[cost.eventId]) {
        grouped[cost.eventId] = {
          eventTitle: cost.eventTitle,
          costs: []
        };
      }
      grouped[cost.eventId].costs.push(cost);
    });
    
    return grouped;
  };

  const groupCostsByBooking = (costs) => {
    const grouped = {};
    
    costs.forEach(cost => {
      if (!grouped[cost.bookingId]) {
        grouped[cost.bookingId] = {
          bookingInfo: {
            serviceName: cost.bookingServiceName,
            serviceType: cost.bookingServiceType,
            bookingDate: cost.bookingDate
          },
          costs: []
        };
      }
      grouped[cost.bookingId].costs.push(cost);
    });
    
    return grouped;
  };

  const extractDescriptionSuggestions = (costsData) => {
    const suggestions = new Set();
    
    costsData.eventCosts?.forEach(cost => {
      if (cost.description && cost.description.trim() !== '') {
        suggestions.add(cost.description);
      }
    });
    
    costsData.serviceCosts?.forEach(cost => {
      if (cost.description && cost.description.trim() !== '') {
        suggestions.add(cost.description);
      }
    });
    
    costsData.bookingCosts?.forEach(cost => {
      if (cost.description && cost.description.trim() !== '') {
        suggestions.add(cost.description);
      }
    });
    
    return Array.from(suggestions);
  };

  const handleSuggestionSelect = (suggestion, formType) => {
    switch (formType) {
      case 'event':
        setNewEventCost(prev => ({ ...prev, description: suggestion }));
        break;
      case 'booking':
        setNewBookingCost(prev => ({ ...prev, description: suggestion }));
        break;
      default:
        break;
    }
    setShowSuggestions(false);
  };

  const addMultiDayCost = (date, cost, description) => {
    const costEntry = {
      date: date.toISOString().split('T')[0],
      cost: parseFloat(cost),
      description: description || `Custo para ${formatDate(date)}`,
      displayDate: formatDate(date)
    };
    
    setMultiDayCosts(prev => [...prev, costEntry]);
  };

  const removeMultiDayCost = (index) => {
    setMultiDayCosts(prev => prev.filter((_, i) => i !== index));
  };

  const saveAllMultiDayCosts = async () => {
    try {
      for (const cost of multiDayCosts) {
        await costService.createEventCost({
          eventId: parseInt(selectedEvent.id),
          cost: cost.cost,
          description: cost.description,
          specificDate: cost.date
        });
      }
      
      setMultiDayCosts([]);
      setSelectedEvent(null);
      setNewEventCost({ eventId: '', cost: '', description: '', specificDate: '' });
      setShowEventForm(false);
      const updatedCosts = await costService.getAllCosts();
    setCosts(prev => ({ ...prev, eventCosts: updatedCosts.eventCosts }));
    
    const suggestions = extractDescriptionSuggestions(updatedCosts);
    setDescriptionSuggestions(suggestions);
      alert(`${multiDayCosts.length} custos multi-dia salvos com sucesso!`);
    } catch (error) {
      console.error('Erro ao salvar custos multi-dia:', error);
      alert('Erro ao salvar custos multi-dia: ' + error.message);
    }
  };

  const handleEventChange = (eventId) => {
    const event = events.find(e => e.id === parseInt(eventId));
    setSelectedEvent(event);
    setMultiDayCosts([]);
    
    if (event) {
      const isMultiDay = event.eventType === 'multi' && event.endDate;
      
      if (isMultiDay) {
        const dates = generateEventDates(event.startDate, event.endDate);
        setSelectedEventDates(dates);
      } else {
        setSelectedEventDates([]);
      }
      
      setNewEventCost(prev => ({
        ...prev,
        eventId,
        specificDate: isMultiDay ? '' : null
      }));
    }
  };

  const loadBookings = async () => {
    try {
      const bookingsData = await bookingService.getAllBookings();
      setBookings(bookingsData);
      
      const bookingCostsData = await costService.getAllBookingCosts();
      setCosts(prev => ({ ...prev, bookingCosts: bookingCostsData }));
    } catch (error) {
      console.error('Erro ao carregar bookings:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [costsData, eventsData] = await Promise.all([
        costService.getAllCosts(),
        eventService.getEvents()
      ]);
      setCosts(costsData);
      setEvents(eventsData);
      
      const suggestions = extractDescriptionSuggestions(costsData);
      setDescriptionSuggestions(suggestions);
      
      await loadBookings();
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEventCost = async (e) => {
    e.preventDefault();
    try {
      if (selectedEvent.eventType === 'multi' && newEventCost.specificDate) {
        const selectedDate = selectedEventDates.find(
          date => date.toISOString().split('T')[0] === newEventCost.specificDate
        );
        
        if (selectedDate) {
          addMultiDayCost(
            selectedDate,
            newEventCost.cost,
            newEventCost.description
          );
          
          setNewEventCost({ 
            ...newEventCost,
            cost: '', 
            description: '',
            specificDate: ''
          });
        }
      } else {
        await costService.createEventCost({
          eventId: parseInt(newEventCost.eventId),
          cost: parseFloat(newEventCost.cost),
          description: newEventCost.description || `Custo do evento ${selectedEvent.title}`,
          specificDate: newEventCost.specificDate || null
        });

        const updatedCosts = await costService.getAllCosts();
      setCosts(prev => ({ ...prev, eventCosts: updatedCosts.eventCosts }));
      
      const suggestions = extractDescriptionSuggestions(updatedCosts);
      setDescriptionSuggestions(suggestions);

      setNewEventCost({ 
        eventId: '', 
        cost: '', 
        description: '',
        specificDate: ''
      });
      setSelectedEvent(null);
      setShowEventForm(false);
      alert('Custo criado com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao criar custo:', error);
      alert('Erro ao criar custo: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleCreateBookingCost = async (e) => {
    e.preventDefault();
    try {
      await costService.createBookingCost({
        bookingId: selectedBooking.id,
        cost: parseFloat(newBookingCost.cost),
        description: newBookingCost.description || `Custo para ${selectedBooking.servicoNome} - ${formatDate(selectedBooking.dataHora)}`
      });

      setNewBookingCost({ cost: '', description: '' });
      await loadBookings();
      alert('Custo específico do booking criado com sucesso!');
    } catch (error) {
      console.error('Erro ao criar custo do booking:', error);
      alert('Erro ao criar custo do booking: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleDeleteCost = async (costId, costType) => {
    if (!window.confirm('Tem certeza que deseja remover este custo?')) {
      return;
    }
    try {
      if (costType === 'event') {
        await costService.deleteEventCost(costId);
        setCosts(prev => ({
          ...prev, 
          eventCosts: prev.eventCosts.filter(cost => cost.id !== costId)
        }));
      } else {
        await costService.deleteServiceCost(costId);
        setCosts(prev => ({
          ...prev,
          serviceCosts: prev.serviceCosts.filter(cost => cost.id !== costId)
        }));
      }
      
      const updatedCosts = await costService.getAllCosts();
      const suggestions = extractDescriptionSuggestions(updatedCosts);
      setDescriptionSuggestions(suggestions);
      
    } catch (error) {
      console.error('Erro ao remover custo:', error);
      alert('Erro ao remover custo');
    }
  };

  const handleDeleteBookingCost = async (costId) => {
    if (!window.confirm('Tem certeza que deseja remover este custo específico?')) {
      return;
    }
    try {
      await costService.deleteBookingCost(costId);
      await loadBookings();
    } catch (error) {
      console.error('Erro ao remover custo do booking:', error);
      alert('Erro ao remover custo do booking');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-PT');
  };

  if (loading) {
    return (
      <div className="costs-management-page">
        <div className="costs-container">
          <div className="loading">
            <p>A carregar custos...</p>
          </div>
        </div>
      </div>
    );
  }

  const groupedEventCosts = groupCostsByEvent(costs.eventCosts || []);
  const groupedBookingCosts = groupCostsByBooking(costs.bookingCosts || []);

  return (
    <div className="costs-management-page">
      <div className="costs-container">
        <div className="costs-header">
          <button className="back-button" onClick={() => navigate('/admin/eventos')}>
            <ArrowLeft size={20} />
            Voltar
          </button>
          <div className="header-content">
            <div>
              <h1 className="costs-title">
                <TrendingDown size={40} />
                Gestão de Custos
              </h1>
              <p className="costs-subtitle">
                Defina os custos operacionais para calcular lucros reais
              </p>
            </div>
          </div>
        </div>

        <div className="costs-sections">
          <div className="cost-section">
            <div className="section-header">
              <h2 className="section-title">
                <DollarSign size={24} />
                Eventos
              </h2>
              <button 
                className="add-cost-btn"
                onClick={() => {
                  setShowEventForm(!showEventForm);
                  setShowBookingForm(false);
                }}
              >
                {showEventForm ? <X size={18} /> : <Plus size={18} />}
                {showEventForm ? 'Cancelar' : 'Adicionar Custo'}
              </button>
            </div>
            <div className="section-content">
              {showEventForm && (
                <form className="cost-form" onSubmit={handleCreateEventCost}>
                  <div className="form-group">
                    <label className="form-label">Evento</label>
                    <select
                      className="form-select"
                      value={newEventCost.eventId}
                      onChange={(e) => handleEventChange(e.target.value)}
                      required
                    >
                      <option value="">Selecione um evento</option>
                      {events.map(event => (
                        <option key={event.id} value={event.id}>
                          {event.title} {event.eventType === 'multi' ? '(Multi-dia)' : '(Dia único)'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedEvent && selectedEvent.eventType === 'multi' && selectedEventDates.length > 0 && (
                    <div className="form-group">
                      <label className="form-label">Selecione a Data</label>
                      <div className="multi-day-info">
                        <h4>Evento Multi-dia</h4>
                        <p>Pode adicionar custos para múltiplas datas. Selecione uma data e adicione o custo.</p>
                      </div>
                      <select
                        className="form-select"
                        value={newEventCost.specificDate}
                        onChange={(e) => setNewEventCost({...newEventCost, specificDate: e.target.value})}
                        required
                      >
                        <option value="">Selecione uma data</option>
                        {selectedEventDates.map((date, index) => (
                          <option key={index} value={date.toISOString().split('T')[0]}>
                            {formatDate(date)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">
                      {selectedEvent?.eventType === 'multi' ? 'Custo para a Data Selecionada (€)' : 'Custo do Evento (€)'}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      value={newEventCost.cost}
                      onChange={(e) => setNewEventCost({...newEventCost, cost: e.target.value})}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Descrição (opcional)</label>
                    <div className="description-input-container">
                      <input
                        type="text"
                        className="form-input"
                        value={newEventCost.description}
                        onChange={(e) => {
                          setNewEventCost({...newEventCost, description: e.target.value});
                          setShowSuggestions(true);
                          setCurrentFormType('event');
                        }}
                        onFocus={() => {
                          setShowSuggestions(true);
                          setCurrentFormType('event');
                        }}
                        placeholder="Ex: Material, equipa, transporte"
                      />
                      <DescriptionSuggestions
                        suggestions={descriptionSuggestions}
                        onSelect={handleSuggestionSelect}
                        visible={showSuggestions && currentFormType === 'event'}
                        formType="event"
                      />
                    </div>
                  </div>

                  {multiDayCosts.length > 0 && (
                    <div className="multi-day-costs-list">
                      <h4>Custos Multi-dia a Guardar ({multiDayCosts.length})</h4>
                      {multiDayCosts.map((cost, index) => (
                        <div key={index} className="multi-day-cost-item">
                          <div className="multi-day-cost-info">
                            <div className="multi-day-cost-date">{cost.displayDate}</div>
                            <div className="cost-desc">{cost.description}</div>
                          </div>
                          <div className="multi-day-cost-value">{formatCurrency(cost.cost)}</div>
                          <div className="multi-day-actions">
                            <button 
                              type="button"
                              className="action-btn delete"
                              onClick={() => removeMultiDayCost(index)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                      <button 
                        type="button"
                        className="save-all-button"
                        onClick={saveAllMultiDayCosts}
                      >
                        <Save size={18} />
                        Guardar Todos os {multiDayCosts.length} Custos
                      </button>
                    </div>
                  )}

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={selectedEvent?.eventType === 'multi' && !newEventCost.specificDate}
                    >
                      <Plus size={18} />
                      {selectedEvent?.eventType === 'multi' ? 'Adicionar à Lista' : 'Guardar Custo'}
                    </button>
                    
                    {selectedEvent?.eventType === 'multi' && multiDayCosts.length > 0 && (
                      <button 
                        type="button"
                        className="continue-button"
                        onClick={() => {
                          setNewEventCost({ 
                            ...newEventCost, 
                            cost: '', 
                            description: '',
                            specificDate: ''
                          });
                        }}
                      >
                        Continuar a Adicionar
                      </button>
                    )}
                    
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowEventForm(false);
                        setNewEventCost({ 
                          eventId: '', 
                          cost: '', 
                          description: '',
                          specificDate: ''
                        });
                        setSelectedEvent(null);
                        setMultiDayCosts([]);
                        setSelectedEventDates([]);
                      }}
                    >
                      <X size={18} />
                      Cancelar
                    </button>
                  </div>
                </form>
              )}

              <div className="costs-list">
                {Object.keys(groupedEventCosts).length > 0 ? (
                  Object.keys(groupedEventCosts).map(eventId => {
                    const group = groupedEventCosts[eventId];
                    return (
                      <div key={eventId} className="cost-group">
                        <div className="cost-group-header">
                          <h3 className="cost-group-title">
                            {group.eventTitle}
                          </h3>
                          <p className="cost-group-subtitle">
                            {group.costs.length} custo(s) definido(s)
                          </p>
                        </div>
                        <div className="section-content">
                          {group.costs.map(cost => (
                            <div key={cost.id} className="cost-item">
                              <div className="cost-info">
                                <div className="cost-name">
                                  {cost.description || 'Custo do evento'}
                                  {cost.specificDate && (
                                    <span style={{color: '#64748b', fontSize: '0.9rem', marginLeft: '0.5rem'}}>
                                      ({formatDate(cost.specificDate)})
                                    </span>
                                  )}
                                </div>
                                <div className="cost-desc">
                                  {cost.specificDate 
                                    ? `Custo específico para ${formatDate(cost.specificDate)}`
                                    : 'Custo padrão do evento'
                                  }
                                </div>
                              </div>
                              <div className="cost-value">
                                {formatCurrency(cost.cost)}
                              </div>
                              <div className="cost-date">
                                <Calendar size={16} />
                                {formatDate(cost.createdAt)}
                              </div>
                              <div></div>
                              <div className="cost-actions">
                                <button 
                                  className="action-btn delete"
                                  onClick={() => handleDeleteCost(cost.id, 'event')}
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-costs">
                    <TrendingDown size={48} />
                    <p>Nenhum custo definido</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="cost-section">
            <div className="section-header">
              <h2 className="section-title">
                <DollarSign size={24} />
                Serviços
              </h2>
              <button 
                className="add-cost-btn"
                onClick={() => {
                  setShowBookingForm(!showBookingForm);
                  setShowEventForm(false);
                }}
              >
                {showBookingForm ? <X size={18} /> : <Plus size={18} />}
                {showBookingForm ? 'Cancelar' : 'Adicionar Custo'}
              </button>
            </div>
            <div className="section-content">
              {showBookingForm && (
                <div className="cost-form">
                  <div className="form-group">
                    <label className="form-label">Selecionar Reserva</label>
                    <select
                      className="form-select"
                      value={selectedBooking ? selectedBooking.id : ''}
                      onChange={(e) => {
                        const bookingId = e.target.value;
                        const booking = bookings.find(b => b.id === parseInt(bookingId));
                        setSelectedBooking(booking);
                      }}
                      required
                    >
                      <option value="">Selecione uma reserva</option>
                      {bookings.map(booking => (
                        <option key={booking.id} value={booking.id}>
                          {booking.servicoNome} ({booking.servicoTipo}) - {formatDate(booking.dataHora)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedBooking && (
                    <>
                      <div className="booking-info">
                        <h4>Informações da Reserva</h4>
                        <p>
                          <strong>Serviço:</strong> {selectedBooking.servicoNome}<br />
                          <strong>Tipo:</strong> {selectedBooking.servicoTipo}<br />
                          <strong>Data:</strong> {formatDate(selectedBooking.dataHora)}<br />
                          <strong>Participantes:</strong> {selectedBooking.numeroParticipantes || 'N/A'}
                        </p>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Custo Específico (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-input"
                          value={newBookingCost.cost}
                          onChange={(e) => setNewBookingCost({...newBookingCost, cost: e.target.value})}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Descrição (opcional)</label>
                        <div className="description-input-container">
                          <input
                            type="text"
                            className="form-input"
                            value={newBookingCost.description}
                            onChange={(e) => {
                              setNewBookingCost({...newBookingCost, description: e.target.value});
                              setShowSuggestions(true);
                              setCurrentFormType('booking');
                            }}
                            onFocus={() => {
                              setShowSuggestions(true);
                              setCurrentFormType('booking');
                            }}
                            placeholder="Ex: Custo específico para esta reserva"
                          />
                          <DescriptionSuggestions
                            suggestions={descriptionSuggestions}
                            onSelect={handleSuggestionSelect}
                            visible={showSuggestions && currentFormType === 'booking'}
                            formType="booking"
                          />
                        </div>
                      </div>
                      <div className="form-actions">
                        <button 
                          type="button" 
                          className="btn btn-primary"
                          onClick={handleCreateBookingCost}
                        >
                          <Save size={18} />
                          Guardar Custo
                        </button>
                        
                        <button 
                          type="button" 
                          className="btn btn-secondary"
                          onClick={() => {
                            setShowBookingForm(false);
                            setSelectedBooking(null);
                            setNewBookingCost({ cost: '', description: '' });
                          }}
                        >
                          <X size={18} />
                          Terminar
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="costs-list">
                {Object.keys(groupedBookingCosts).length > 0 ? (
                  Object.keys(groupedBookingCosts).map(bookingId => {
                    const group = groupedBookingCosts[bookingId];
                    return (
                      <div key={bookingId} className="cost-group">
                        <div className="cost-group-header">
                          <h3 className="cost-group-title">
                            {group.bookingInfo.serviceName}
                          </h3>
                          <p className="cost-group-subtitle">
                            {formatDate(group.bookingInfo.bookingDate)} • {group.costs.length} custo(s)
                          </p>
                        </div>
                        <div className="section-content">
                          {group.costs.map(cost => (
                            <div key={cost.id} className="cost-item">
                              <div className="cost-info">
                                <div className="cost-name">
                                  {cost.description || 'Custo específico'}
                                </div>
                                <div className="cost-desc">
                                  {group.bookingInfo.serviceType} • {formatDate(cost.bookingDate)}
                                </div>
                              </div>
                              <div className="cost-value">
                                {formatCurrency(cost.cost)}
                              </div>
                              <div className="cost-date">
                                <Calendar size={16} />
                                {formatDate(cost.createdAt)}
                              </div>
                              <div></div>
                              <div className="cost-actions">
                                <button 
                                  className="action-btn delete"
                                  onClick={() => handleDeleteBookingCost(cost.id)}
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-costs">
                    <TrendingDown size={32} />
                    <p>Nenhum custo específico definido</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}